import { Badge } from '@dreamtree-org/twreact-ui';
import { SectionCard, KeyValue, StatusBadge, QueryState, Money, EmptyState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatINR, formatDate } from '@/lib/format';
import DataTable from '../../components/DataTable';
import { usePlatformCan } from '../../lib/access';

const SETTLED = { payout: 'Deducted from payout', recorded: 'Payment recorded', waived: 'Waived' };

/** The store's plan and its platform invoices (billed on the 1st, netted from payouts — D20). */
export default function SubscriptionTab({ merchant: m }) {
  const canBilling = usePlatformCan('plans.view');
  const plans = useQuery('platformPlans', undefined, { select: (r) => r.data });
  const plan = (plans.data || []).find((p) => p.key === m.plan_key);
  const invoices = useQuery('platformMerchantInvoices', { id: m.id }, { select: (r) => r.data, enabled: canBilling });

  return (
    <div className="space-y-4">
      <QueryState query={plans}>
        {() => (
          <SectionCard title="Plan" actions={plan ? <Badge variant="success" size="sm">v{plan.version}</Badge> : null}>
            {plan ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <div className="text-lg font-semibold">{plan.name}</div>
                  <div className="text-2xl font-bold">{formatINR(plan.price_paise)}<span className="text-sm font-normal opacity-60"> /{plan.billing_period}</span></div>
                  <ul className="text-sm opacity-80 space-y-1 pt-2">{(plan.features || []).map((f) => <li key={f}>✓ {f}</li>)}</ul>
                </div>
                <KeyValue items={[
                  { label: 'Plan key', value: plan.key },
                  { label: 'Unit limit', value: plan.max_units == null ? 'Unlimited' : plan.max_units },
                  { label: 'Merchant status', value: <StatusBadge status={m.status} /> },
                  { label: 'Since', value: formatDate(m.created_at) },
                ]} />
              </div>
            ) : <p className="text-sm opacity-70">Plan “{m.plan_key}” is not in the active plan list.</p>}
          </SectionCard>
        )}
      </QueryState>

      <SectionCard title="Invoices">
        {!canBilling ? <p className="text-sm opacity-70">Invoices need the plans.view permission.</p> : (
          <QueryState query={invoices}>
            {(rows) => (rows.length ? (
              <DataTable
                data={rows}
                columns={[
                  { key: 'id', label: 'Invoice', render: (r) => <span className="font-medium">{r.id}</span> },
                  { key: 'period', label: 'Period' },
                  { key: 'amount_paise', label: 'Amount', hideOnMobile: true, render: (r) => <Money paise={r.amount_paise} /> },
                  { key: 'gst_paise', label: 'GST', hideOnMobile: true, render: (r) => <Money paise={r.gst_paise} /> },
                  { key: 'total_paise', label: 'Total', render: (r) => <Money paise={r.total_paise} className="font-medium" /> },
                  { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
                  { key: 'settled_via', label: 'Settled', hideOnMobile: true, wrap: true, render: (r) => (r.settled_via ? `${SETTLED[r.settled_via] || r.settled_via}${r.settlement_ref ? ` · ${r.settlement_ref}` : ''}` : `Due ${formatDate(r.due_at)}`) },
                ]}
              />
            ) : <EmptyState title="No invoices yet">The first invoice is issued by the next sweep after the store goes live.</EmptyState>)}
          </QueryState>
        )}
      </SectionCard>
    </div>
  );
}
